import { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { InferType, mixed, object, string } from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { toast } from "react-toastify";
import { t } from "i18next";
import { Trans } from "react-i18next";
import { tv } from "tailwind-variants";

import { usePostBasicStyling } from "@/requests/hooks/comply";
import fontList from "@/assets/fonts/google-font-list.json";

import { Card } from "@/components/Card";
import { H5, P } from "@/components/Typography";
import ColorPicker from "@/components/ColorPicker";
import { Select } from "@/components/Select";
import ImageUpload from "@/components/ImageUpload";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { readFileAsBase64 } from "@/utils/readFileAsBase64";
const supportedFormat = ["image/jpg", "image/jpeg", "image/png"];
const maxLogoFileSize = 100 * 1024;
const maxBannerImageFileSize = 1024 * 1024;
const defaultPrimaryColour = "#10A4FF";
const defaultSecondaryColour = "#F2F5F7";
const defaultCtaColour = "#00243A";
const colorHashRegex = /^#/;

const styles = tv({
  slots: {
    base: "flex flex-col gap-30",
    section: "px-20 py-30",
    brandHeading: "mb-25 font-bold",
    brandContent:
      "grid grid-cols-1 items-start justify-start gap-y-10 md:grid-cols-2 lg:flex lg:flex-row lg:gap-x-10",
    dropDownContainer: "mt-1 flex w-full flex-col",
    dropDownLabel: "mb-8 h-15 text-sm font-normal leading-4",
    imageHeading: "mb-15 font-bold",
    privacyEmailHeading: "mb-10 font-bold",
    privacyInputContainer: "max-w-full md:max-w-[384px]",
    buttonContainer: "mb-20 mt-10 flex justify-center",
  },
});

declare interface IProps {
  basicStylingData: IBasicStylingRSData;
}

const ThemeForm = ({ basicStylingData }: IProps) => {
  const {
    base,
    section,
    brandHeading,
    brandContent,
    dropDownContainer,
    dropDownLabel,
    imageHeading,
    privacyEmailHeading,
    privacyInputContainer,
    buttonContainer,
  } = styles();

  const formSchema = object({
    primaryColour: string()
      .required(t("comply.privacyCenter.theme.form.errors.colourRequired"))
      .matches(
        colorHashRegex,
        t("comply.privacyCenter.theme.form.errors.colourCode"),
      )
      .test(
        "COLOR_CODE_LENGTH",
        t("comply.privacyCenter.theme.form.errors.colourLength"),
        value => value?.length === 4 || value?.length === 7,
      ),
    secondaryColour: string()
      .required(t("comply.privacyCenter.theme.form.errors.colourRequired"))
      .matches(
        colorHashRegex,
        t("comply.privacyCenter.theme.form.errors.colourCode"),
      )
      .test(
        "COLOR_CODE_LENGTH",
        t("comply.privacyCenter.theme.form.errors.colourLength"),
        value => value?.length === 4 || value?.length === 7,
      ),
    ctaColour: string()
      .required(t("comply.privacyCenter.theme.form.errors.colourRequired"))
      .matches(
        colorHashRegex,
        t("comply.privacyCenter.theme.form.errors.colourCode"),
      )
      .test(
        "COLOR_CODE_LENGTH",
        t("comply.privacyCenter.theme.form.errors.colourLength"),
        value => value?.length === 4 || value?.length === 7,
      ),
    font: object()
      .shape({
        label: string().required(),
        value: string().required(),
      })
      .required(t("comply.privacyCenter.theme.form.errors.fontRequired")),
    logo: string().nullable(),
    logoImageFile: mixed()
      .nullable()
      .test(
        "FILE_SIZE",
        t("comply.privacyCenter.theme.form.errors.imageFileSize"),
        value => !value || (value && (value as File).size <= maxLogoFileSize),
      )
      .test(
        "FILE_FORMAT",
        t("comply.privacyCenter.theme.form.errors.imageFileFormat"),
        value =>
          !value ||
          (value &&
            supportedFormat.includes((value as File).type?.toLowerCase())),
      )
      .transform(value => value && value[0]),

    bannerImage: string().nullable(),
    bannerImageFile: mixed()
      .nullable()
      .test(
        "FILE_SIZE",
        t("comply.privacyCenter.theme.form.errors.imageFileSize"),
        value =>
          !value || (value && (value as File).size <= maxBannerImageFileSize),
      )
      .test(
        "FILE_FORMAT",
        t("comply.privacyCenter.theme.form.errors.imageFileFormat"),
        value =>
          !value ||
          (value &&
            supportedFormat.includes((value as File).type?.toLowerCase())),
      )
      .transform(value => value && value[0]),
    privacyEmail: string()
      .email(t("comply.privacyCenter.theme.form.errors.emailInvalid"))
      .required(t("comply.privacyCenter.theme.form.errors.emailRequired")),
  });

  type IThemeForm = InferType<typeof formSchema>;

  const {
    mutate: postBasicStyling,
    isPending: isPostBasicStylingPending,
    isSuccess: isPostBasicStylingSuccess,
    data: postBasicStylingVersion,
  } = usePostBasicStyling();

  const {
    primaryColour,
    secondaryColour,
    ctaColour,
    font,
    logo,
    bannerImage,
    privacyEmail,
    version,
  } = basicStylingData;

  const {
    handleSubmit,
    control,
    register,
    trigger,
    watch,
    setValue,
    formState: { errors },
  } = useForm<IThemeForm>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      primaryColour: primaryColour || defaultPrimaryColour,
      secondaryColour: secondaryColour || defaultSecondaryColour,
      ctaColour: ctaColour || defaultCtaColour,
      font: {
        label: font,
        value: font,
      },
      logo: logo,
      logoImageFile: undefined,
      bannerImage: bannerImage,
      bannerImageFile: undefined,
      privacyEmail: privacyEmail as string,
    },
  });

  const getFonts = () =>
    fontList.map(item => ({
      label: item,
      value: item,
    }));

  const handleOnSubmitAddCustomer = async ({
    primaryColour,
    secondaryColour,
    ctaColour,
    font,
    logoImageFile,
    bannerImageFile,
    privacyEmail,
    logo,
    bannerImage,
  }: IThemeForm) => {
    const data: IBasicStyling = {
      primaryColour,
      secondaryColour,
      ctaColour,
      font: font.value,
      privacyEmail: privacyEmail,
      version: postBasicStylingVersion?.version || version,
      logoFile: logo,
      bannerImageFile: bannerImage,
    };

    if (logoImageFile) {
      data.logoFile = await readFileAsBase64(logoImageFile as File);
    }
    if (bannerImageFile) {
      data.bannerImageFile = await readFileAsBase64(bannerImageFile as File);
    }

    postBasicStyling(data);
  };

  const handleClearLogo = () => {
    setValue("logo", undefined);
    setValue("logoImageFile", undefined);
  };

  const handleClearBannerImage = () => {
    setValue("bannerImage", undefined);
    setValue("bannerImageFile", undefined);
  };

  useEffect(() => {
    if (isPostBasicStylingSuccess)
      toast.success(t("comply.privacyCenter.theme.success"));
  }, [isPostBasicStylingSuccess]);

  useEffect(() => {
    trigger();
  }, [watch("logoImageFile"), watch("bannerImageFile"), watch("privacyEmail")]);

  const renderDropDown = () => (
    <div className={dropDownContainer()} data-testid="theme-font">
      <div className={dropDownLabel()}>
        {t("comply.privacyCenter.theme.form.font")}
      </div>
      <Controller
        name="font"
        control={control}
        render={({ field }) => (
          <Select
            {...field}
            label=""
            options={getFonts()}
            helperText={errors?.font?.message}
            variant={errors?.font && "error"}
            aria-invalid={!!errors?.font}
            closeMenuOnSelect
          />
        )}
      />
    </div>
  );

  const renderBrandSection = () => {
    return (
      <Card rounded="sm">
        <div className={section()} data-testid="theme-brand-section">
          <H5 className={brandHeading()}>
            {t("comply.privacyCenter.theme.section.brand.title")}
          </H5>
          <div className={brandContent()}>
            <Controller
              name="primaryColour"
              control={control}
              render={({ field }) => (
                <ColorPicker
                  label={t("comply.privacyCenter.theme.form.primaryColour")}
                  helperText={errors?.primaryColour?.message}
                  variant={errors?.primaryColour && "error"}
                  testId="theme-primary-colour"
                  {...field}
                />
              )}
            />

            <Controller
              name="secondaryColour"
              control={control}
              render={({ field }) => (
                <ColorPicker
                  label={t("comply.privacyCenter.theme.form.secondaryColour")}
                  helperText={errors?.secondaryColour?.message}
                  variant={errors?.secondaryColour && "error"}
                  testId="theme-secondary-colour"
                  {...field}
                />
              )}
            />

            <Controller
              name="ctaColour"
              control={control}
              render={({ field }) => (
                <ColorPicker
                  label={t("comply.privacyCenter.theme.form.ctaColour")}
                  helperText={errors?.ctaColour?.message}
                  variant={errors?.ctaColour && "error"}
                  testId="theme-cta-colour"
                  {...field}
                />
              )}
            />

            <div className="w-full">{renderDropDown()}</div>
          </div>
        </div>
      </Card>
    );
  };

  const renderLogoSection = () => (
    <Card rounded="sm">
      <div className={section()} data-testid="theme-logo-section">
        <H5 className={imageHeading()}>
          {t("comply.privacyCenter.theme.section.logo.title")}
        </H5>
        <P className="mb-10">
          <Trans
            i18nKey={"comply.privacyCenter.theme.section.logo.description"}
          />
        </P>
        <ImageUpload
          image={logo}
          type="logo"
          helperText={errors?.logoImageFile?.message}
          testId="theme-logo-img"
          onClear={handleClearLogo}
          {...register("logoImageFile")}
        />
      </div>
    </Card>
  );

  const renderBannerSection = () => (
    <Card rounded="sm">
      <div className={section()} data-testid="theme-banner-section">
        <H5 className={imageHeading()}>
          {t("comply.privacyCenter.theme.section.banner.title")}
        </H5>
        <P className="mb-10">
          <Trans
            i18nKey={"comply.privacyCenter.theme.section.banner.description"}
          />
        </P>
        <ImageUpload
          image={bannerImage}
          type="banner"
          helperText={errors?.bannerImageFile?.message}
          testId="theme-banner-img"
          onClear={handleClearBannerImage}
          {...register("bannerImageFile")}
        />
      </div>
    </Card>
  );

  const renderPrivacyEmailSection = () => (
    <Card rounded="sm">
      <div className={section()} data-testid="theme-privacy-email-section">
        <H5 className={privacyEmailHeading()}>
          {t("comply.privacyCenter.theme.section.privacyEmail.title")}
        </H5>
        <P className="mb-20">
          {t("comply.privacyCenter.theme.section.privacyEmail.description")}
        </P>
        <div className={privacyInputContainer()}>
          <Input
            label={t("comply.privacyCenter.theme.form.privacyEmail")}
            {...register("privacyEmail")}
            testId="privacy-email-input"
            helperText={errors?.privacyEmail?.message}
            variant={errors?.privacyEmail?.message ? "error" : undefined}
          />
        </div>
      </div>
    </Card>
  );

  const renderButton = () => (
    <div className={buttonContainer()}>
      <Button
        type="submit"
        loading={isPostBasicStylingPending}
        testId="theme-save-btn">
        {t("comply.privacyCenter.theme.form.saveButton")}
      </Button>
    </div>
  );

  return (
    <form className={base()} onSubmit={handleSubmit(handleOnSubmitAddCustomer)}>
      {renderBrandSection()}
      {renderLogoSection()}
      {renderBannerSection()}
      {renderPrivacyEmailSection()}
      {renderButton()}
    </form>
  );
};

export default ThemeForm;
